/**
 * LogicSetDiagramReadOnly
 * PBI 266671 - Logic Set dynamic palette information
 *
 * Read-only version of the Logic Set diagram that reuses
 * the same styled components as the canvas for visual consistency.
 */

import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import EdgeImg from 'components/feature/EdgeImg'
import { mergedConditionSelector } from 'selectors/feature/conditions'
import { getSignal } from 'selectors/feature/inputs'
import conditionLabelText from 'components/feature/utils/conditionLabelText'
import {
  COMPLEX,
  SUBTYPES_WITH_HYSTERESIS,
  CARRY_TIMER,
  CARRY_LOGIC_SET,
  SELF_REFERENCE,
  TIMER,
  CARRY,
} from 'utils/constants'
import { truncateText } from 'utils/utils'
import { OPERATORS } from 'components/utils'
// Import Figma-spec gate and delay icon components for modal
import {
  getModalGateComponent,
  getModalFinalGateComponent,
  ActivateDelayIcon,
  DeactivateDelayIcon,
} from './LogicGates'

import {
  DiagramWrapper,
  LogicSetContainer,
  LogicSetHeader,
  ConditionsColumn,
  ReadOnlyConditionBox,
  ReadOnlyConditionContent,
  ReadOnlyConditionText,
  ReadOnlyConditionIcons,
  ReadOnlyHysteresisText,
  DelayIconsContainer,
  DelayIconWrapper,
  GatesContainer,
  GateWrapper,
  ConnectingLinesSVG,
} from './LogicSetDiagramReadOnly.style'

const { LESS_THAN, GREATER_THAN_OR_EQUAL } = OPERATORS

/**
 * Determines if hysteresis should be shown for a condition
 * This mirrors the logic from ConditionLabel.jsx
 */
const shouldShowHysteresis = (subType, carryType, parentType, operator) => {
  if (parentType === TIMER) return false

  if (!SUBTYPES_WITH_HYSTERESIS.has(subType)) return false

  if (carryType !== CARRY_TIMER && subType === CARRY) return false

  const numOperator = parseInt(operator, 10)

  return (
    parentType === COMPLEX &&
    (numOperator === LESS_THAN || numOperator === GREATER_THAN_OR_EQUAL)
  )
}

/**
 * Custom Logic Gates component with SVG connecting lines for modal
 * This replaces the canvas LogicGates to have precise control over line positioning
 */
const ReadOnlyLogicGates = ({
  abComplexQueryOperator,
  cdComplexQueryOperator,
  finalComplexQueryOperator,
  selectedElement,
  hoveredElement,
  onSelectElement,
  onHoverElement,
  onLeaveElement,
}) => {
  const TopGate = getModalGateComponent(abComplexQueryOperator)
  const BottomGate = getModalGateComponent(cdComplexQueryOperator)
  const FinalGate = getModalFinalGateComponent(finalComplexQueryOperator)

  // Check if gates are selected
  const isTopGateSelected = selectedElement === 'gate-top'
  const isBottomGateSelected = selectedElement === 'gate-bottom'
  const isFinalGateSelected = selectedElement === 'gate-middle'

  // Check if gates are hovered
  const isTopGateHovered = hoveredElement === 'gate-top'
  const isBottomGateHovered = hoveredElement === 'gate-bottom'
  const isFinalGateHovered = hoveredElement === 'gate-middle'

  // Gate dimensions - Figma specs
  const gateWidth = 68
  const gateHeight = 52
  const horizontalGap = 30 // Space between intermediate and final gate

  // Condition layout:
  // - Each condition is 84px tall with -2px margin overlap
  // - Top pair (conditions 0-1): 0 to ~166px
  // - 27px gap
  // - Bottom pair (conditions 2-3): ~193px to ~359px
  const conditionHeight = 84
  const conditionOverlap = 2
  const pairGap = 27

  // Calculate gate positions to center on each condition pair
  const topPairCenter = conditionHeight - conditionOverlap / 2 // Center of first 2 conditions
  const bottomPairStart = conditionHeight * 2 - conditionOverlap + pairGap
  const bottomPairCenter =
    bottomPairStart + conditionHeight - conditionOverlap / 2

  // Positions - center gates on their respective pairs
  const topGateY = topPairCenter - gateHeight / 2
  const bottomGateY = bottomPairCenter - gateHeight / 2
  const finalGateX = gateWidth + horizontalGap
  const finalGateY = (topGateY + bottomGateY + gateHeight) / 2 - gateHeight / 2

  // Final gate SVG dimensions (includes output line and arrow)
  const finalGateWidth = 186

  // Container dimensions
  const containerWidth = finalGateX + finalGateWidth
  // Total height based on 4 conditions with gap
  const totalHeight = conditionHeight * 4 - conditionOverlap * 3 + pairGap

  // SVG dimensions for connecting lines
  const svgWidth = containerWidth

  // Line styling - Figma specs (gray default, blue when hovered, dark blue when selected)
  const defaultLineColor = '#687585'
  const hoverLineColor = '#1E7AFF'
  const selectedLineColor = '#0051C4'
  const lineWidth = 4

  // Line colors change when top/bottom gates are selected or hovered
  const getLineColor = (isSelected, isHovered) => {
    if (isSelected) return selectedLineColor
    if (isHovered) return hoverLineColor
    return defaultLineColor
  }
  const topLineColor = getLineColor(isTopGateSelected, isTopGateHovered)
  const bottomLineColor = getLineColor(
    isBottomGateSelected,
    isBottomGateHovered
  )

  // Calculate line connection points
  const topGateCenterY = topGateY + gateHeight / 2
  const bottomGateCenterY = bottomGateY + gateHeight / 2

  // Final gate input line positions (scaled for 68x52 gate)
  const finalGateTopInputY = finalGateY + 15
  const finalGateBottomInputY = finalGateY + 37
  const curveRadius = 9 // Figma spec radius

  return (
    <GatesContainer
      style={{
        width: containerWidth,
        height: totalHeight,
        minHeight: totalHeight,
      }}
    >
      {/* SVG for connecting lines - overflows container for output line */}
      <ConnectingLinesSVG
        viewBox={`0 0 ${svgWidth} ${totalHeight}`}
        style={{ width: svgWidth, overflow: 'visible' }}
      >
        {/* Line from top gate to final gate's top input */}
        <path
          d={`M ${gateWidth} ${topGateCenterY}
              L ${gateWidth + 8} ${topGateCenterY}
              Q ${gateWidth + 18} ${topGateCenterY} ${gateWidth + 18} ${
            topGateCenterY + curveRadius
          }
              L ${gateWidth + 18} ${finalGateTopInputY - curveRadius}
              Q ${gateWidth + 18} ${finalGateTopInputY} ${
            gateWidth + 18 + curveRadius
          } ${finalGateTopInputY}
              L ${finalGateX + 20} ${finalGateTopInputY}`}
          stroke={topLineColor}
          strokeWidth={lineWidth}
          fill="none"
        />
        {/* Line from bottom gate to final gate's bottom input */}
        <path
          d={`M ${gateWidth} ${bottomGateCenterY}
              L ${gateWidth + 8} ${bottomGateCenterY}
              Q ${gateWidth + 18} ${bottomGateCenterY} ${gateWidth + 18} ${
            bottomGateCenterY - curveRadius
          }
              L ${gateWidth + 18} ${finalGateBottomInputY + curveRadius}
              Q ${gateWidth + 18} ${finalGateBottomInputY} ${
            gateWidth + 18 + curveRadius
          } ${finalGateBottomInputY}
              L ${finalGateX + 20} ${finalGateBottomInputY}`}
          stroke={bottomLineColor}
          strokeWidth={lineWidth}
          fill="none"
        />
      </ConnectingLinesSVG>

      {/* Top gate (ab operator) */}
      <GateWrapper
        style={{
          position: 'absolute',
          top: topGateY,
          left: 0,
          cursor: 'pointer',
        }}
        onClick={() => onSelectElement('gate-top')}
        onMouseEnter={() => onHoverElement('gate-top')}
        onMouseLeave={onLeaveElement}
      >
        <TopGate selected={isTopGateSelected} hovered={isTopGateHovered} />
      </GateWrapper>

      {/* Bottom gate (cd operator) */}
      <GateWrapper
        style={{
          position: 'absolute',
          top: bottomGateY,
          left: 0,
          cursor: 'pointer',
        }}
        onClick={() => onSelectElement('gate-bottom')}
        onMouseEnter={() => onHoverElement('gate-bottom')}
        onMouseLeave={onLeaveElement}
      >
        <BottomGate
          selected={isBottomGateSelected}
          hovered={isBottomGateHovered}
        />
      </GateWrapper>

      {/* Final gate (xyz operator) - includes output line and arrow */}
      <div
        style={{
          position: 'absolute',
          top: finalGateY,
          left: finalGateX,
          cursor: 'pointer',
        }}
        onClick={() => onSelectElement('gate-middle')}
        onMouseEnter={() => onHoverElement('gate-middle')}
        onMouseLeave={onLeaveElement}
      >
        <FinalGate
          selected={isFinalGateSelected}
          hovered={isFinalGateHovered}
        />
      </div>
    </GatesContainer>
  )
}

ReadOnlyLogicGates.propTypes = {
  abComplexQueryOperator: PropTypes.string,
  cdComplexQueryOperator: PropTypes.string,
  finalComplexQueryOperator: PropTypes.string,
  selectedElement: PropTypes.string,
  hoveredElement: PropTypes.string,
  onSelectElement: PropTypes.func,
  onHoverElement: PropTypes.func,
  onLeaveElement: PropTypes.func,
}

/**
 * Hook to get condition details from Redux state
 * Uses targeted selectors to avoid re-renders on unrelated state changes
 */
const useConditionDetails = (conditionUri) => {
  // Select only the condition data we need for this specific URI
  const conditionData = useSelector((state) => {
    if (!conditionUri) return null

    const mergedCondition = mergedConditionSelector(state, {
      queryUri: conditionUri,
    })
    if (!mergedCondition || mergedCondition.size === 0) return null

    const data = mergedCondition.toJS ? mergedCondition.toJS() : mergedCondition

    const signalUri = data.signal
    const signal = signalUri ? getSignal(state, { signal: signalUri }) : {}

    // If no input is selected, show empty label instead of "- Undefined -"
    const formattedLabel = data.selectedInputDescription
      ? conditionLabelText({
          ...data,
          signal,
          ignoreCarry: false,
        })
      : ''

    return {
      formattedLabel,
      selectedInputDescription: data.selectedInputDescription,
      operator: data.operator,
      value: data.value,
      rungType: data.rungType,
      isCarry:
        data.rungType === CARRY_LOGIC_SET ||
        data.rungType === CARRY_TIMER ||
        data.rungType === SELF_REFERENCE,
      edgeTriggerType: data.edge || 'none',
      hysteresis: data.hysteresis,
      subType: data.subType,
      carryType: data.carryType,
      parentType: data.parentType,
    }
  })

  return conditionData
}

/**
 * Read-only Condition component that matches canvas styling
 */
const ReadOnlyCondition = ({
  conditionUri,
  index,
  totalConditions,
  isSelected,
  isHovered,
  onSelect,
  onHover,
  onLeave,
}) => {
  const conditionData = useConditionDetails(conditionUri)

  if (!conditionUri || !conditionData) return null

  // Only first and last conditions get rounded corners
  const isFirst = index === 0
  const isLast = index === totalConditions - 1
  // Second element of each pair (indices 1, 3) uses "bottom" border-radius style
  const isBottomPair = index % 2 === 1

  const {
    formattedLabel,
    selectedInputDescription,
    edgeTriggerType,
    hysteresis,
    subType,
    carryType,
    parentType,
    operator,
  } = conditionData
  // Match canvas ConditionLabel guard: only show icons when input is selected
  const hasDescription = Boolean(selectedInputDescription)
  const hasHysteresis = shouldShowHysteresis(
    subType,
    carryType,
    parentType,
    operator
  )

  // Map edge trigger values to EdgeImg expected values
  // Data can come in storage format (NoEdge, RisingEdge) or lowercase (none, rising)
  const edgeMap = {
    none: 'NoEdge',
    rising: 'RisingEdge',
    falling: 'FallingEdge',
    both: 'BothEdges',
    NoEdge: 'NoEdge',
    RisingEdge: 'RisingEdge',
    FallingEdge: 'FallingEdge',
    BothEdges: 'BothEdges',
  }
  const edgeImgValue = edgeMap[edgeTriggerType] || 'NoEdge'

  return (
    <ReadOnlyConditionBox
      isFirst={isFirst}
      isLast={isLast}
      isBottomPair={isBottomPair}
      isSelected={isSelected}
      isHovered={isHovered}
      onClick={onSelect}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      <ReadOnlyConditionContent>
        <ReadOnlyConditionText>{formattedLabel}</ReadOnlyConditionText>
        <ReadOnlyConditionIcons>
          {hasDescription &&
            (hasHysteresis ? (
              <ReadOnlyHysteresisText>
                H: {hysteresis || 0}
              </ReadOnlyHysteresisText>
            ) : (
              <EdgeImg edge={edgeImgValue} />
            ))}
        </ReadOnlyConditionIcons>
      </ReadOnlyConditionContent>
    </ReadOnlyConditionBox>
  )
}

ReadOnlyCondition.propTypes = {
  conditionUri: PropTypes.string,
  index: PropTypes.number.isRequired,
  totalConditions: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  isHovered: PropTypes.bool,
  onSelect: PropTypes.func,
  onHover: PropTypes.func,
  onLeave: PropTypes.func,
}

/**
 * Logic Set Diagram Read-Only Component
 * Renders the logic set diagram using the same visual style as the canvas
 */
const LogicSetDiagramReadOnly = ({
  logicSetName,
  conditions,
  abComplexQueryOperator,
  cdComplexQueryOperator,
  finalComplexQueryOperator,
  delayActivate,
  delayDeactivate,
  selectedElement,
  hoveredElement,
  onSelectElement,
  onHoverElement,
  onLeaveElement,
}) => {
  const activeConditions = conditions.filter(Boolean)
  const conditionCount = activeConditions.length

  // Show gates when there's more than one condition
  const hasGates = conditionCount > 1

  const isConditionSelected = (index) =>
    selectedElement === `condition-${index}`
  const isConditionHovered = (index) => hoveredElement === `condition-${index}`
  const isDelaySelected = (type) => selectedElement === `delay-${type}`
  const isDelayHovered = (type) => hoveredElement === `delay-${type}`

  return (
    <DiagramWrapper>
      {/* Delay icons in top right - 50px hit target per Figma spec */}
      {(delayActivate > 0 || delayDeactivate > 0) && (
        <DelayIconsContainer>
          {delayActivate > 0 && (
            <DelayIconWrapper
              onClick={() => onSelectElement('delay-activate')}
              onMouseEnter={() => onHoverElement('delay-activate')}
              onMouseLeave={onLeaveElement}
              title="Activate Delay"
            >
              <ActivateDelayIcon
                selected={isDelaySelected('activate')}
                hovered={isDelayHovered('activate')}
              />
            </DelayIconWrapper>
          )}
          {delayDeactivate > 0 && (
            <DelayIconWrapper
              onClick={() => onSelectElement('delay-deactivate')}
              onMouseEnter={() => onHoverElement('delay-deactivate')}
              onMouseLeave={onLeaveElement}
              title="Deactivate Delay"
            >
              <DeactivateDelayIcon
                selected={isDelaySelected('deactivate')}
                hovered={isDelayHovered('deactivate')}
              />
            </DelayIconWrapper>
          )}
        </DelayIconsContainer>
      )}

      {/* Logic Set container with title - matching canvas style */}
      <LogicSetContainer>
        <LogicSetHeader>{truncateText(logicSetName, 40)}</LogicSetHeader>

        {/* Main diagram content - flex container matching canvas StyledContent exactly */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            position: 'relative',
            boxSizing: 'border-box',
            fontSize: '12px',
          }}
        >
          {/* Conditions column - all conditions stacked together */}
          <ConditionsColumn>
            {conditions.filter(Boolean).map((conditionUri, index) => (
              <React.Fragment key={conditionUri}>
                {/* Add 27px gap between condition pairs (after index 1) */}
                {index === 2 && <div style={{ height: '27px' }} />}
                <ReadOnlyCondition
                  conditionUri={conditionUri}
                  index={index}
                  totalConditions={conditionCount}
                  isSelected={isConditionSelected(index)}
                  isHovered={isConditionHovered(index)}
                  onSelect={() => onSelectElement(`condition-${index}`)}
                  onHover={() => onHoverElement(`condition-${index}`)}
                  onLeave={onLeaveElement}
                />
              </React.Fragment>
            ))}
          </ConditionsColumn>

          {/* Logic gates - custom component with SVG connecting lines */}
          {hasGates && (
            <ReadOnlyLogicGates
              abComplexQueryOperator={abComplexQueryOperator}
              cdComplexQueryOperator={cdComplexQueryOperator}
              finalComplexQueryOperator={finalComplexQueryOperator}
              selectedElement={selectedElement}
              hoveredElement={hoveredElement}
              onSelectElement={onSelectElement}
              onHoverElement={onHoverElement}
              onLeaveElement={onLeaveElement}
            />
          )}
        </div>
      </LogicSetContainer>
    </DiagramWrapper>
  )
}

LogicSetDiagramReadOnly.propTypes = {
  logicSetName: PropTypes.string,
  conditions: PropTypes.arrayOf(PropTypes.string),
  abComplexQueryOperator: PropTypes.string,
  cdComplexQueryOperator: PropTypes.string,
  finalComplexQueryOperator: PropTypes.string,
  delayActivate: PropTypes.number,
  delayDeactivate: PropTypes.number,
  selectedElement: PropTypes.string,
  hoveredElement: PropTypes.string,
  onSelectElement: PropTypes.func,
  onHoverElement: PropTypes.func,
  onLeaveElement: PropTypes.func,
}

LogicSetDiagramReadOnly.defaultProps = {
  logicSetName: 'Logic Set',
  conditions: [],
  abComplexQueryOperator: 'or',
  cdComplexQueryOperator: 'or',
  finalComplexQueryOperator: 'or',
  delayActivate: 0,
  delayDeactivate: 0,
  selectedElement: null,
  hoveredElement: null,
  onSelectElement: () => {},
  onHoverElement: () => {},
  onLeaveElement: () => {},
}

export default LogicSetDiagramReadOnly
